import React from "react";
import { TextField, InputAdornment } from "@mui/material";
import { Search } from "lucide-react";

interface HeaderSearchProps {
    value?: string;
    onSearch: (value: string) => void;
    placeholder?: string;
    debounceMs?: number;
    width?: number | string;
    // Extra control rendered inside the box at its right edge - e.g. a
    // search-scope toggle, so it reads as part of the search itself.
    endAdornment?: React.ReactNode;
}

// A compact, debounced keyword search box for a page header - LIKE '%keyword%'
// on whatever field(s) the caller wires it to, so it covers both partial and
// full matches. Debounced rather than committing on every keystroke so typing
// doesn't fire a request (or client-side refilter) per character; still
// re-syncs from `value` if the underlying filter gets cleared/changed
// elsewhere (e.g. a column-header filter popup for the same field).
const HeaderSearch: React.FC<HeaderSearchProps> = ({ value, onSearch, placeholder = "Search...", debounceMs = 400, width = 220, endAdornment }) => {
    const [inputValue, setInputValue] = React.useState(value || "");
    const lastCommittedRef = React.useRef(value || "");

    React.useEffect(() => {
        setInputValue(value || "");
        lastCommittedRef.current = value || "";
    }, [value]);

    React.useEffect(() => {
        const handle = setTimeout(() => {
            if (inputValue !== lastCommittedRef.current) {
                lastCommittedRef.current = inputValue;
                onSearch(inputValue);
            }
        }, debounceMs);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputValue, debounceMs]);

    return (
        <TextField
            size="small"
            placeholder={placeholder}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            sx={{
                width,
                background: "var(--color-page-background)",
                "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
            }}
            InputProps={{
                sx: { fontSize: 13 },
                startAdornment: (
                    <InputAdornment position="start">
                        <Search size={16} color="var(--color-primary)" />
                    </InputAdornment>
                ),
                ...(endAdornment ? {
                    endAdornment: (
                        <InputAdornment position="end" sx={{ ml: 0.5, pl: 1, borderLeft: '1px solid rgba(0,0,0,0.12)', height: 24, maxHeight: 'none' }}>
                            {endAdornment}
                        </InputAdornment>
                    ),
                } : {}),
            }}
        />
    );
};

export default HeaderSearch;
